/* smellBgone — AJAX cart drawer.

   - The header cart icon (`[data-cart-toggle]`) opens the `.smb-cart-dropdown`,
     a fixed panel down the right edge, instead of navigating to /cart. With no
     JS the icon stays a plain link, so the drawer (which starts `hidden`)
     never shows.
   - After an add, the drawer names what went in: refresh() diffs each line's
     quantity before and after, and the next open() shows the line that grew
     in [data-cart-added] and marks it .is-just-added. Every add path calls
     refresh().then(open), so none of them has to pass the product.
   - Quantity +/- and remove POST to /cart/change.js, then the drawer is
     refreshed by re-fetching the header section via the Section Rendering API
     (`?section_id=header`) and swapping the `[data-cart-drawer]` inner content.
     This mirrors the change.js + section-rendering pattern in main-cart.liquid.
   - The drawer stays open across the refresh (only the inner content is swapped,
     never the open state), and the `[data-cart-count]` badge is updated. */
(function () {
  if (window.__smbCartDrawerInit) return;
  window.__smbCartDrawerInit = true;

  // The drawer is rendered inside the header section (its refresh asks for
  // ?section_id=header), but it must not live inside <header>: the header's
  // hide-on-scroll sets a transform on it, and a transformed ancestor
  // captures a position: fixed child, so the drawer scrolled away with the
  // header. It moves to <body> here; refresh() swaps its inner content
  // wherever it lives. The theme editor re-renders the header section with
  // a fresh drawer inside, so that one replaces the moved one.
  function adoptDrawer() {
    var header = document.querySelector('[data-smb-header]');
    var fresh = header && header.querySelector('[data-cart-drawer]');
    if (!fresh) return;
    document.querySelectorAll('[data-cart-drawer-moved]').forEach(function (old) {
      old.remove();
    });
    fresh.setAttribute('data-cart-drawer-moved', '');
    document.body.appendChild(fresh);
  }
  adoptDrawer();
  document.addEventListener('shopify:section:load', adoptDrawer);

  var DURATION = 320; // matches --duration-base; fallback close timer.
  var pendingAdded = []; // the lines an add just grew, until open() shows them
  var returnFocus = null;

  function drawer() {
    return document.querySelector('[data-cart-drawer]');
  }
  function toggle() {
    return document.querySelector('[data-cart-toggle]');
  }
  function backdrop() {
    return document.querySelector('[data-cart-backdrop]');
  }

  function isOpen() {
    var d = drawer();
    return !!d && d.classList.contains('is-open');
  }

  function lineQuantities(root) {
    var map = {};
    if (!root) return map;
    root.querySelectorAll('[data-drawer-line]').forEach(function (li) {
      map[li.getAttribute('data-drawer-line')] = parseInt(li.getAttribute('data-drawer-qty'), 10) || 0;
    });
    return map;
  }

  function showAdded(d) {
    var status = d.querySelector('[data-cart-added]');
    if (!status) return;
    var keys = pendingAdded;
    pendingAdded = [];
    d.querySelectorAll('.is-just-added').forEach(function (el) {
      el.classList.remove('is-just-added');
    });
    var names = [];
    keys.forEach(function (key) {
      var line = d.querySelector('[data-drawer-line="' + (window.CSS && CSS.escape ? CSS.escape(key) : key) + '"]');
      if (!line) return;
      line.classList.add('is-just-added');
      // The plain name the section prints for this (the title's own text
      // would drop the wordmark's B, which is a picture).
      names.push((line.getAttribute('data-drawer-name') || '').trim());
    });
    var text = status.querySelector('[data-cart-added-text]');
    if (!names.length || !text) {
      status.hidden = true;
      return;
    }
    // Every line the add grew: one name, or "A and B" for a kit, or "A, B
    // and C".
    var joined = names.length === 1 ? names[0] : names.slice(0, -1).join(', ') + ' and ' + names[names.length - 1];
    text.textContent = 'Added: ' + joined;
    status.hidden = false;
  }

  function openDrawer() {
    var d = drawer();
    if (!d) return;
    showAdded(d);
    if (isOpen()) return;
    returnFocus = document.activeElement;
    d.hidden = false;
    var b = backdrop();
    if (b) b.hidden = false;
    var t = toggle();
    if (t) t.setAttribute('aria-expanded', 'true');
    // Force reflow so the slide-in runs from the closed state.
    void d.offsetHeight;
    d.classList.add('is-open');
    // The page behind stays put while the drawer is open (brand-ext.css).
    document.documentElement.classList.add('smb-cart-open');
    var title = d.querySelector('#smb-cart-drawer-title');
    if (title) title.focus({ preventScroll: true });
  }

  function closeDrawer() {
    var d = drawer();
    if (!d || !isOpen()) return;
    d.classList.remove('is-open');
    document.documentElement.classList.remove('smb-cart-open');
    var b = backdrop();
    if (b) b.hidden = true;
    var t = toggle();
    if (t) t.setAttribute('aria-expanded', 'false');
    // Restore `hidden` once collapsed, so no-JS parity + focus order are kept.
    var done = false;
    function finish(e) {
      if (done || (e && e.target !== d)) return;
      done = true;
      if (!d.classList.contains('is-open')) d.hidden = true;
      d.removeEventListener('transitionend', finish);
    }
    d.addEventListener('transitionend', finish);
    setTimeout(finish, DURATION + 60);
    if (d.contains(document.activeElement)) {
      // Back to whatever opened it; the cart icon when that was nothing
      // focusable (Safari does not focus a clicked button).
      var back = returnFocus && returnFocus !== document.body && document.contains(returnFocus) ? returnFocus : toggle();
      if (back) back.focus({ preventScroll: true });
    }
    returnFocus = null;
  }

  function updateCount(count) {
    document.querySelectorAll('[data-cart-count]').forEach(function (el) {
      el.textContent = count;
    });
    var t = toggle();
    if (t) t.setAttribute('aria-label', 'Cart (' + count + ' items)');
  }

  // Re-render the header section and swap only the drawer's inner content,
  // preserving the live element (and its open state).
  function refresh() {
    return fetch(window.location.pathname + '?section_id=header', {
      headers: { Accept: 'text/html' },
    })
      .then(function (r) {
        return r.text();
      })
      .then(function (html) {
        var doc = new DOMParser().parseFromString(html, 'text/html');
        var fresh = doc.querySelector('[data-cart-drawer]');
        var current = drawer();
        if (fresh && current) {
          var before = lineQuantities(current);
          var after = lineQuantities(fresh);
          pendingAdded = Object.keys(after).filter(function (key) {
            return after[key] > (before[key] || 0);
          });
          var hadFocus = current.contains(document.activeElement);
          current.innerHTML = fresh.innerHTML;
          if (hadFocus) {
            var title = current.querySelector('#smb-cart-drawer-title');
            if (title) title.focus({ preventScroll: true });
          }
        }
        var freshCount = doc.querySelector('[data-cart-count]');
        if (freshCount) updateCount(freshCount.textContent.trim());
      })
      .catch(function () {
        window.location.reload();
      });
  }

  function change(key, quantity) {
    fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ id: key, quantity: quantity }),
    })
      .then(function (r) {
        return r.json();
      })
      .then(function (cart) {
        updateCount(cart.item_count);
        return refresh();
      })
      .then(function () {
        // A + inside the drawer is not an add to announce.
        pendingAdded = [];
        var status = drawer() && drawer().querySelector('[data-cart-added]');
        if (status) status.hidden = true;
      })
      .catch(function () {
        window.location.reload();
      });
  }

  // Special-instructions note — mirrors the Hydrogen CartNote (NoteUpdate).
  function updateNote(value) {
    fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ note: value }),
    })
      .then(function () {
        return refresh();
      })
      .catch(function () {
        window.location.reload();
      });
  }

  /* Open/close — the cart icon is an <a href="/cart"> intercepted here. */
  document.addEventListener('click', function (e) {
    if (e.target.closest('[data-cart-toggle]')) {
      e.preventDefault();
      if (isOpen()) closeDrawer();
      else openDrawer();
      return;
    }
    if (e.target.closest('[data-cart-backdrop]') || e.target.closest('[data-cart-close]')) {
      closeDrawer();
      return;
    }

    // Save the special-instructions note (delegated — survives content swaps).
    if (e.target.closest('[data-drawer-note-save]')) {
      e.preventDefault();
      var noteInput = document.querySelector('[data-drawer-note-input]');
      updateNote(noteInput ? noteInput.value : '');
      return;
    }

    // Line-item controls (delegated — survives inner-content swaps).
    var line = e.target.closest('[data-drawer-line]');
    if (!line) return;
    var key = line.getAttribute('data-drawer-line');
    var qty = parseInt(line.getAttribute('data-drawer-qty'), 10) || 0;

    if (e.target.closest('[data-drawer-remove]')) {
      e.preventDefault();
      change(key, 0);
    } else if (e.target.closest('[data-drawer-plus]')) {
      e.preventDefault();
      change(key, qty + 1);
    } else if (e.target.closest('[data-drawer-minus]')) {
      e.preventDefault();
      if (qty > 1) change(key, qty - 1);
    }
  });

  // Discount code — apply via Shopify's /discount/{code} route, then return to
  // the current page (mirrors the Hydrogen CartDiscounts DiscountCodesUpdate).
  document.addEventListener('submit', function (e) {
    var form = e.target.closest('[data-drawer-discount]');
    if (!form) return;
    e.preventDefault();
    var input = form.querySelector('[data-drawer-discount-input]');
    var code = input && input.value.trim();
    if (!code) return;
    window.location.href = '/discount/' + encodeURIComponent(code) + '?redirect=' + encodeURIComponent(window.location.pathname);
  });

  document.addEventListener('keydown', function (e) {
    if (e.key === 'Escape' && isOpen()) closeDrawer();
  });

  // Exposed so other add-to-cart flows (the PDP form in smb-product.js) can
  // pull the drawer's real content in and open it, instead of only patching
  // the header badge and leaving the drawer's initial server-rendered
  // (possibly empty-cart) markup stale until a full page reload.
  window.smbCartDrawer = { refresh: refresh, open: openDrawer };
})();
